<script>
import baseMixin from '@/mixins/baseMixin'
import formMixin from '@/mixins/formMixin'
import { mapActions, mapGetters } from 'vuex'
import ThePasswordRulesIndicator from '../general/the-password-rules-indicator.vue'

export default {
  name: 'ChangePasswordContainer',
  components: { ThePasswordRulesIndicator },
  mixins: [baseMixin, formMixin],
  data: () => {
    return {
      newPass: null,
      confirmPass: null,
      currentPassword: null,
      isValid: true,
      showNewPass: false,
      showConfirmPass: false,
      showCurrentPassword: false,
      errors: null,
      formChanged: false,
      failedToLoad: false,
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'auth/isLoadingAuth',
    }),
  },
  created() {
    // ? Unable to change password if impersonting as it requires Firebase auth
    if (this.isImpersonating) {
      alert(
        'Unable to change password if impersonating a contact. You will be redirected back to settings.'
      )
      this.$router.push({ name: 'settings' })
    }
  },
  methods: {
    ...mapActions({
      changePasswordAsync: 'auth/changePasswordAsync',
      reauthAsync: 'auth/reauthenticateWithCredentialsAsync',
    }),
    validate() {
      return this.$refs.changePasswordForm.validate()
    },
    resetForm() {
      return this.$refs.changePasswordForm.reset()
    },
    clearError() {
      this.errors = null
    },
    setFormChanged() {
      this.isValid = this.validate()
      this.formChanged = true
    },
    onDismissed() {
      this.clearError()
    },
    async updatePassword() {
      this.clearError()

      if (!this.isValid) return false

      const payload = {
        newPass: this.newPass,
        confirmPass: this.confirmPass,
        currentPassword: this.currentPassword,
      }

      // Reauthenticate. Security requirement for firebase
      const reauthResult = await this.reauthAsync(payload.currentPassword)

      if (!reauthResult.isSuccess) {
        this.errors = {
          message: this.$t('settings.changePasswordCurrPasswordInvalid'),
        }
        return
      }

      const result = await this.changePasswordAsync(payload)

      if (!result.isSuccess) {
        this.errors = result.errors
        return
      }

      this.resetForm()
      this.formChanged = false
    },
  },
}
</script>

<template>
  <div id="edit-profile" class="settings-card">
    <!-- Reauth failure -->
    <v-alert
      v-if="errors"
      type="error"
      dismissible
      text
      @dismissed="onDismissed"
    >
      {{ errors.message }}
    </v-alert>

    <v-card :loading="isLoading" class="pt-0 pb-0 my-0 rounded-xl cl-elevation">
      <v-card-text>
        <v-form
          ref="changePasswordForm"
          lazy-validation
          :value="isValid"
          autocomplete="off"
        >
          <v-row>
            <v-col v-if="!isMobileViewPort" cols="12" md="4" sm="12" xs="12">
              <div class="mt-8">
                {{ $t('input.currentPasswordLabel') }}
                <span class="error--text">*</span>
              </div>
            </v-col>
            <v-col cols="12" md="8" sm="12" xs="12">
              <v-text-field
                id="currentPassword"
                v-model="currentPassword"
                color="indigo"
                class="mb-2"
                name="currentPassword"
                :label="
                  isMobileViewPort ? $t('input.currentPasswordLabel') : ''
                "
                :disabled="isLoading"
                :append-icon="showCurrentPassword ? 'mdi-eye-off' : 'mdi-eye'"
                :rules="[rules.required]"
                :type="showCurrentPassword ? 'text' : 'password'"
                aria-autocomplete="current-password"
                autocomplete="current-password"
                @input="setFormChanged"
                @click:append="showCurrentPassword = !showCurrentPassword"
              >
              </v-text-field>
            </v-col>
            <v-col v-if="!isMobileViewPort" cols="12" md="4" sm="12" xs="12">
              <div class="mt-8">
                {{ $t('input.newPasswordLabel') }}
                <span class="error--text">*</span>
              </div>
            </v-col>
            <v-col cols="12" md="8" sm="12" xs="12">
              <v-text-field
                id="newPass"
                v-model="newPass"
                color="indigo"
                class="mb-2"
                :label="isMobileViewPort ? $t('input.newPasswordLabel') : ''"
                name="newPass"
                :disabled="isLoading"
                :append-icon="showNewPass ? 'mdi-eye-off' : 'mdi-eye'"
                :rules="[
                  rules.required,
                  rules.uppercase,
                  rules.lowercase,
                  rules.number,
                  rules.special,
                  rules.min,
                ]"
                :type="showNewPass ? 'text' : 'password'"
                aria-autocomplete="new-password"
                autocomplete="new-password"
                counter
                @input="setFormChanged"
                @click:append="showNewPass = !showNewPass"
              >
              </v-text-field>

              <ThePasswordRulesIndicator
                :class="isMobileViewPort ? 'mt-6 mb-4' : ''"
                :password="newPass"
                :cols="1"
              />
            </v-col>
          </v-row>

          <v-row align="center">
            <v-col v-if="!isMobileViewPort" cols="12" md="4" sm="12" xs="12">
              <div>
                {{ $t('input.confirmPasswordLabel') }}
                <span class="error--text">*</span>
              </div>
            </v-col>
            <v-col cols="12" md="8" sm="12" xs="12">
              <v-text-field
                id="confirmPass"
                v-model="confirmPass"
                color="indigo"
                class="mb-2"
                :label="
                  isMobileViewPort ? $t('input.confirmPasswordLabel') : ''
                "
                name="confirmPass"
                :disabled="isLoading"
                :append-icon="showConfirmPass ? 'mdi-eye-off' : 'mdi-eye'"
                :rules="[rules.required, rules.passwordsMatch]"
                :type="showConfirmPass ? 'text' : 'password'"
                aria-autocomplete="new-password"
                autocomplete="new-password"
                counter
                @input="setFormChanged"
                @click:append="showConfirmPass = !showConfirmPass"
              >
              </v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          id="back-to-settings-btn"
          :to="{ name: 'settings' }"
          text
          class="rounded-lg"
          exact
          large
        >
          {{ $t('general.cancelBtnText') }}
        </v-btn>

        <v-btn
          id="change-password-button"
          text
          color="blue"
          :loading="isLoading"
          class="rounded-lg ml-auto"
          :disabled="!isValid || !formChanged"
          large
          @click="updatePassword"
        >
          {{ $t('general.saveBtnLabel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>
